<template>
  <header class="sticky top-0 z-40 bg-blue-600 shadow-md">
    <div class="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between gap-4">
        <!-- Logo and Title -->
        <div class="flex min-w-0 items-center gap-3">
          <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20">
            <DocumentTextIcon class="h-6 w-6 text-white" />
          </div>
          <div class="min-w-0">
            <p class="truncate text-base font-bold leading-tight text-white sm:text-lg">Portal de Proveedores</p>
            <p class="hidden truncate text-xs text-blue-100 sm:block">Sistema de Gestión de Facturas</p>
          </div>
        </div>

        <!-- Navigation and Actions -->
        <div class="flex items-center gap-3">
          <!-- Cambio de vista (solo admins): muestra en cuál estás y lleva a la otra -->
          <nav
            v-if="authStore.isAdmin"
            aria-label="Cambiar de vista"
            class="hidden h-10 items-stretch rounded-lg bg-white/10 p-1 ring-1 ring-inset ring-white/15 md:flex"
          >
            <RouterLink
              v-for="view in VIEWS"
              :key="view.name"
              :to="view.to"
              :aria-current="currentView === view.name ? 'page' : undefined"
              :class="viewLinkClass(view.name)"
            >
              <component :is="view.icon" class="h-4 w-4" />
              {{ view.label }}
            </RouterLink>
          </nav>

          <!-- Company Selector (Admin Only) -->
          <CompanySelector v-if="authStore.isAdmin" />

          <!-- User Menu -->
          <!-- Sin invitado: el 404 se renderiza con header y mostraba un menu
               de usuario vacio con "Cerrar Sesion". -->
          <Menu v-if="authStore.isAuthenticated" as="div" class="relative">
            <MenuButton
              :aria-label="`Menú de ${authStore.userName}`"
              class="flex h-10 items-center gap-3 rounded-lg px-2 text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <span
                class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold ring-1 ring-inset ring-white/25"
                aria-hidden="true"
              >
                {{ userInitials }}
              </span>
              <!-- El nombre solo cabe junto al selector de empresa en pantallas anchas -->
              <span class="hidden min-w-0 text-left xl:block">
                <span class="block max-w-[12rem] truncate text-sm font-medium leading-tight">{{ shortUserName }}</span>
                <span class="block text-xs leading-tight text-blue-100">{{ roleLabel }}</span>
              </span>
              <ChevronDownIcon class="h-4 w-4 text-blue-100" />
            </MenuButton>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
              >
                <div class="border-b border-gray-100 px-4 py-3">
                  <p class="text-sm font-medium text-gray-900">{{ authStore.userName }}</p>
                  <p v-if="authStore.userEmail" class="truncate text-xs text-gray-500">{{ authStore.userEmail }}</p>
                  <p class="mt-1 text-xs font-medium text-blue-600">{{ roleLabel }}</p>
                </div>
                <div class="p-1">
                  <MenuItem v-slot="{ active }">
                    <button
                      type="button"
                      :class="[
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-red-600',
                        active ? 'bg-red-50' : ''
                      ]"
                      @click="authStore.logout"
                    >
                      <ArrowRightOnRectangleIcon class="h-5 w-5" />
                      Cerrar sesión
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation: el mismo cambio de vista, a lo ancho -->
    <nav
      v-if="authStore.isAdmin"
      aria-label="Cambiar de vista"
      class="border-t border-white/10 px-4 py-2 md:hidden"
    >
      <div class="flex h-10 items-stretch rounded-lg bg-white/10 p-1 ring-1 ring-inset ring-white/15">
        <RouterLink
          v-for="view in VIEWS"
          :key="view.name"
          :to="view.to"
          :aria-current="currentView === view.name ? 'page' : undefined"
          :class="[viewLinkClass(view.name), 'flex-1 justify-center']"
        >
          <component :is="view.icon" class="h-4 w-4" />
          {{ view.label }}
        </RouterLink>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import { useAuthStore } from "@/stores/auth";

import {
  DocumentTextIcon,
  UserIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon
} from "@heroicons/vue/24/outline";

import CompanySelector from "@/components/common/CompanySelector.vue";

const VIEWS = [
  { name: "admin", label: "Admin", to: "/admin", icon: Cog6ToothIcon },
  { name: "supplier", label: "Proveedor", to: "/supplier", icon: UserIcon }
];

const currentRoute = useRoute();
const authStore = useAuthStore();

// Cualquier ruta que no sea la del proveedor cuenta como vista admin
const currentView = computed(() => (currentRoute.name === "supplier" ? "supplier" : "admin"));

const viewLinkClass = (viewName) => [
  "flex items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
  currentView.value === viewName
    ? "bg-white text-blue-700 shadow-sm"
    : "text-blue-100 hover:bg-white/10 hover:text-white"
];

const roleLabel = computed(() => (authStore.isAdmin ? "Administrador" : "Proveedor"));

const firstWord = (text) => (text || "").trim().split(/\s+/)[0] || "";

// En el botón va primer nombre + primer apellido; el completo, en el menú
const shortUserName = computed(() => {
  const { firstName, lastName } = authStore.user || {};
  return `${firstWord(firstName)} ${firstWord(lastName)}`.trim();
});

// Inicial del nombre y del apellido: "Oscar Eduardo" + "Mazariegos López" -> "OM"
const userInitials = computed(() => {
  const { firstName, lastName } = authStore.user || {};
  return `${firstWord(firstName).charAt(0)}${firstWord(lastName).charAt(0)}`.toUpperCase();
});
</script>
